import { Clock } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { DateHelper } from "@/utils"

import { OrdersStatusBadge } from "./status-badge"
import { getAdditionalDetail } from "./status-details"
import { getStatusConfig } from "./status-tracking-config"

type EventItem = {
  status: string
  description: string
  statusDetails?: string
  createdAt: string
}

type StatusTrackingSheetProps = {
  status: string
  description?: string
  code: string
  events?: EventItem[]
}

export const StatusTrackingSheet = ({
  status,
  description,
  code,
  events = [],
}: StatusTrackingSheetProps) => {
  const { t } = useTranslation()
  const hasEvents = events.length > 0

  return (
    <Sheet>
      <div className="flex items-center gap-2">
        <OrdersStatusBadge
          status={{
            status,
            description: description ?? "",
          }}
        />
        {hasEvents && (
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs md:hidden"
            >
              <Clock size={12} />
              {t("orders.statusTracking.viewHistory")}
            </Button>
          </SheetTrigger>
        )}
      </div>
      <SheetContent
        side="bottom"
        className="flex max-h-[70vh] flex-col rounded-t-xl"
      >
        <SheetHeader className="flex-shrink-0 pb-4">
          <SheetTitle className="flex items-center gap-2">
            {t("orders.statusTracking.title")}
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            {t("orders.statusTracking.orderCode")}: {code}
          </p>
        </SheetHeader>

        {hasEvents ? (
          <div className="min-h-0 flex-1 space-y-0 overflow-y-auto pb-4 pr-2">
            {events.map((event, index) => {
              const isLast = index === events.length - 1
              const statusColor = getStatusConfig(
                event.status
              ).bgColor

              return (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={`h-3 w-3 rounded-full ${statusColor}`}
                    />
                    {!isLast && (
                      <div className="h-full w-0.5 bg-border" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <p className="text-sm font-medium">
                      {event.description}
                    </p>
                    {getAdditionalDetail(
                      event.description,
                      event.statusDetails
                    ) && (
                      <p className="mt-1 rounded-md border-l-2 border-amber-400 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                        {getAdditionalDetail(
                          event.description,
                          event.statusDetails
                        )}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {DateHelper.formatDateTime(
                        event.createdAt,
                        "dd/MM/yyyy HH:mm"
                      )}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-muted-foreground">
              {t("orders.statusTracking.noHistory")}
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
